import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { SxProps } from '@mui/material';
import {
  PointOfInterest,
  FileUploadStatus,
  useUploadAttachmentsToPoiMutation,
  SelectedPointOfInterestDocument,
} from '@/__generated__/graphql';
import { usePoiPostInspectionNoteMutation } from '@/hooks/usePoiPostInspectionNoteMutation';
import { useHavePermission } from '@/hooks';
import { usePoiAttachments } from '@/hooks/usePoiAttachments';
import { poiAttachmentsAtom } from '@/components/Analysis/modules/pointOfInterest/state/atoms';
import { Permissions } from '@/types';
import { LocationTag, MAX_FILE_UPLOAD_LIMIT } from '@/constants';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { MediaWrapperContainer } from './styles';
import { AttachmentsViewer } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/Tabs/AttachmentsViewer';
import validateFiles from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/Tabs/ValidateFiles';
import { SectionMediaNote } from './SectionMediaNote';
import Button from '@/components/shared/Button';

type Props = {
  selectedPoiInfo: PointOfInterest;
  isUpdating?: boolean;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  isEditMode?: boolean;
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  sx?: SxProps;
  sectionKey?: string;
};

export const SectionMedia = ({
  selectedPoiInfo,
  isUpdating: externalIsUpdating,
  setIsUpdating: externalSetIsUpdating,
  isEditMode = false,
  saveRef,
  sectionKey,
}: Props) => {
  const { updateNote } = usePoiPostInspectionNoteMutation();
  const userCanUploadMedia = useHavePermission(Permissions.CREATE_UPLOAD_MEDIA);
  const [internalIsUpdating, setInternalIsUpdating] = useState(false);
  const isUpdating = externalIsUpdating ?? internalIsUpdating;
  const setIsUpdating = externalSetIsUpdating ?? setInternalIsUpdating;
  const [uploadAttachmentsToPoi] = useUploadAttachmentsToPoiMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  usePoiAttachments({
    selectedPoiInfo,
    isUpdating,
  });

  const allPoiMedia = useAtomValue(poiAttachmentsAtom);
  const sectionMedia = sectionKey
    ? allPoiMedia.filter((a) => a.groupKey === sectionKey)
    : allPoiMedia;
  const setPoiAttachments = useSetAtom(poiAttachmentsAtom);

  const initialNote = selectedPoiInfo?.notes[sectionKey ?? ''] ?? '';
  const [pendingNote, setPendingNote] = useState<string>(initialNote);
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [pendingNewAttachments, setPendingNewAttachments] = useState<Attachment[]>([]);
  const mediaUpload = useCallback(
    async (filesToUpload: Attachment[]) => {
      setIsUpdating(true);
      setPoiAttachments((prev) => [...prev, ...filesToUpload]);
      try {
        const { data } = await uploadAttachmentsToPoi({
          variables: {
            input: {
              pointOfInterestId: selectedPoiInfo.id,
              files: filesToUpload.map(({ uuid, name, type, size }) => ({
                uuid: uuid || '',
                name,
                size: size || 0,
                mimeType: type,
                groupKey: sectionKey || '',
              })),
              lastModifiedFor: 'Media Upload',
            },
          },
          onError: (error) => {
            showSnackBar(error.message, 'error');
            setPoiAttachments((prev) =>
              prev.filter((a) => !filesToUpload.some((f) => f.uuid === a.uuid))
            );
            setIsUpdating(false);
          },
        });

        if (data?.uploadAttachmentsToPoi?.length) {
          setPoiAttachments((prev) =>
            prev.map((attachment) => {
              const uploaded = data.uploadAttachmentsToPoi?.find((u) => u.uuid === attachment.uuid);
              if (uploaded) {
                return {
                  ...attachment,
                  url: uploaded.url,
                  resourcePath: uploaded.resourcePath,
                  id: uploaded.id,
                };
              }
              return attachment;
            })
          );
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        showSnackBar('Failed to upload files', 'error');
        setPoiAttachments((prev) =>
          prev.filter((a) => !filesToUpload.some((f) => f.uuid === a.uuid))
        );
        setIsUpdating(false);
      }
    },
    [
      setIsUpdating,
      setPoiAttachments,
      uploadAttachmentsToPoi,
      selectedPoiInfo.id,
      showSnackBar,
      sectionKey,
    ]
  );

  const handleFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      const totalFiles = sectionMedia.length + pendingNewAttachments.length + (files?.length || 0);
      if (totalFiles > MAX_FILE_UPLOAD_LIMIT) {
        showSnackBar(
          `You can only upload a maximum of ${MAX_FILE_UPLOAD_LIMIT} files in total.`,
          'error'
        );
        return;
      }
      if (files && files.length > 0) {
        const fileArray = [...files];
        const { validFiles, fileErrors } = validateFiles(fileArray);
        if (fileErrors.length > 0) {
          fileErrors.forEach((error) => showSnackBar(error, 'error'));
          return;
        }

        const allExisting = [...sectionMedia, ...pendingNewAttachments];
        const duplicateFiles = validFiles.filter((file) =>
          allExisting.some((att) => att.name === file.name.replace(/[^\w.-]/g, '_'))
        );
        if (duplicateFiles.length > 0) {
          showSnackBar(
            `Duplicate file names found: ${duplicateFiles.map((f) => f.name).join(', ')}`,
            'error'
          );
          return;
        }

        const newAttachments: Attachment[] = validFiles.map((file, index) => ({
          uuid: `file-${Date.now()}-${index}`,
          name: file.name,
          type: file.type,
          isNew: true,
          size: file.size,
          file,
          uploadStatus: FileUploadStatus.Inprogress,
          groupKey: sectionKey,
        }));
        if (isEditMode) {
          setPendingNewAttachments((prev) => [...prev, ...newAttachments]);
        } else {
          await mediaUpload(newAttachments);
        }
      }
      event.target.value = '';
    },
    [sectionMedia, pendingNewAttachments, showSnackBar, isEditMode, mediaUpload, sectionKey]
  );

  const handleRemovePendingAttachment = useCallback((index: number) => {
    setPendingNewAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    setIsUpdating(true);
    try {
      if (pendingNote !== initialNote && pendingNewAttachments.length === 0) {
        if (sectionKey) {
          await uploadAttachmentsToPoi({
            variables: {
              input: {
                pointOfInterestId: selectedPoiInfo.id,
                files: [],
                lastModifiedFor: 'Update section note',
                groupKeyNotes: [{ groupKey: sectionKey, note: pendingNote }],
              },
            },
            refetchQueries: [SelectedPointOfInterestDocument],
          });
        } else {
          await updateNote(pendingNote, selectedPoiInfo.id);
        }
      }

      if (pendingNewAttachments.length > 0) {
        setPoiAttachments((prev) => [...prev, ...pendingNewAttachments]);

        const notePayload =
          pendingNote !== initialNote && sectionKey
            ? { groupKeyNotes: [{ groupKey: sectionKey, note: pendingNote }] }
            : {};

        const { data } = await uploadAttachmentsToPoi({
          variables: {
            input: {
              pointOfInterestId: selectedPoiInfo.id,
              files: pendingNewAttachments.map(({ uuid, name, type, size }) => ({
                uuid: uuid || '',
                name,
                size: size || 0,
                mimeType: type,
                groupKey: sectionKey || '',
              })),
              lastModifiedFor: 'Media Upload',
              ...notePayload,
            },
          },
          refetchQueries: [SelectedPointOfInterestDocument],
          onError: (error) => {
            showSnackBar(error.message, 'error');
            setIsUpdating(false);
          },
        });

        if (data?.uploadAttachmentsToPoi?.length) {
          setPoiAttachments((prev) =>
            prev.map((attachment) => {
              const uploaded = data.uploadAttachmentsToPoi?.find((u) => u.uuid === attachment.uuid);
              if (uploaded) {
                return {
                  ...attachment,
                  url: uploaded.url,
                  resourcePath: uploaded.resourcePath,
                  id: uploaded.id,
                  uploadStatus: FileUploadStatus.Success,
                };
              }
              return attachment;
            })
          );
        }
      } else {
        setIsUpdating(false);
      }

      showSnackBar('Changes saved successfully', 'success');
    } catch (error) {
      console.error('Error saving changes:', error);
      showSnackBar('Failed to save changes', 'error');
      setIsUpdating(false);
    } finally {
      setPendingNewAttachments([]);
      setIsNoteEditing(false);
    }
  }, [
    setIsUpdating,
    pendingNote,
    initialNote,
    pendingNewAttachments,
    showSnackBar,
    updateNote,
    selectedPoiInfo.id,
    setPoiAttachments,
    uploadAttachmentsToPoi,
    sectionKey,
  ]);

  useEffect(() => {
    if (saveRef) {
      saveRef.current = handleSave;
    }
  }, [saveRef, handleSave]);

  return (
    <MediaWrapperContainer
      sx={{
        flex: 1,
        gap: '1.6rem',
      }}
      className="mediaViewer"
    >
      <SectionMediaNote
        note={pendingNote}
        onNoteChange={setPendingNote}
        isEditing={isNoteEditing}
        onEditingChange={(editing) => {
          setIsNoteEditing(editing);
          if (!editing && !isEditMode && pendingNote !== initialNote) {
            handleSave();
          }
        }}
        canEdit={userCanUploadMedia}
      />

      <input
        ref={fileInputRef}
        accept="image/jpeg,image/png,application/pdf,video/mp4"
        style={{ display: 'none' }}
        multiple
        type="file"
        onChange={handleFileSelect}
      />

      {userCanUploadMedia && (
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          startIcon={<AbyssIcon iconName="load-view" size="sm" />}
        >
          Upload or drag file
        </Button>
      )}

      <AttachmentsViewer
        attachments={sectionMedia}
        allAttachments={sectionMedia}
        setPoiAttachments={setPoiAttachments}
        selectedPoiInfo={selectedPoiInfo}
        setIsUpdating={setIsUpdating}
        isUpdating={isUpdating}
        location={LocationTag.ViewerSelectedPoiDetails}
        layout="list"
      />

      {pendingNewAttachments.length > 0 &&
        pendingNewAttachments.map((attachment, index) => (
          <FileDisplay
            key={attachment.uuid || `pending-${index}`}
            collectionName="poi"
            selectedAttachment={attachment}
            selectedAttachmentIndex={index}
            handleRemoveAttachment={handleRemovePendingAttachment}
            showRemoveIcon
            location={LocationTag.ViewerSelectedPoiDetails}
            layout="list"
          />
        ))}
    </MediaWrapperContainer>
  );
};
